import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Checkbox,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { IconArrowLeft, IconInbox, IconPlus, IconRefresh, IconSearch, IconTrash, IconX } from "@tabler/icons";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import ActionButton, { iconButtonSx } from "src/components/shared/ActionButton";
import RowTable from "./RowTable";

// The CMS list table: title, search, add, bulk delete, sort, pagination and
// the five states (loading, empty, error, rows, busy row). Every page passes
// the same props it always did; the new ones are optional:
//   subtitle           one line under the title saying what the list is for
//   addLabel           the add button's words (default "Add new")
//   searchPlaceholder  what the search box looks for (default "Search")
//   loadError          true or a sentence when the list did not load
//   onRetry            what "Try again" does when loadError is set
//   getRowLabel(row)   names a row for screen readers ("Actions for …")

function EnhancedTableHead({ onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, headCells, withDeleteItem, withReorder }) {
  return (
    <TableHead>
      <TableRow>
        {withReorder && (
          <TableCell padding="checkbox">
            <Box component="span" sx={visuallyHidden}>
              Reorder
            </Box>
          </TableCell>
        )}
        {withDeleteItem && (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              disabled={rowCount === 0}
              inputProps={{ "aria-label": "Select every row on this page" }}
            />
          </TableCell>
        )}
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            className={headCell.id === "action" ? "table-sticky-actions" : undefined}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={headCell.withSort && orderBy === headCell.id ? order : false}
          >
            {headCell.id === "action" ? (
              <Box component="span" sx={visuallyHidden}>
                {headCell.label || "Actions"}
              </Box>
            ) : headCell.withSort ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={(event) => onRequestSort(event, headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onSelectAllClick: PropTypes.func,
  order: PropTypes.string,
  orderBy: PropTypes.string,
  numSelected: PropTypes.number,
  rowCount: PropTypes.number,
  onRequestSort: PropTypes.func,
  headCells: PropTypes.array,
  withDeleteItem: PropTypes.bool,
  withReorder: PropTypes.bool,
};

function Toolbar({
  numSelected,
  handleSearch,
  search,
  onPressDelete,
  selected,
  onPressAdd,
  setSelected,
  withSearch,
  withAddButton,
  withDeleteItem,
  canBack,
  nameTable,
  subtitle,
  addLabel,
  searchPlaceholder,
}) {
  const navigate = useNavigate();
  const selecting = numSelected > 0 && withDeleteItem;
  const hasTitle = nameTable != null && nameTable !== "";
  return (
    <>
      {/* One page head everywhere: title and purpose on the left, the
          primary action top-right; search sits on the row below. */}
      {(hasTitle || canBack) && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, sm: 2 }}
          sx={{ mb: 2.5 }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ minWidth: 0 }}>
            {canBack && (
              <Tooltip title="Back">
                <IconButton aria-label="Back" onClick={() => navigate(-1)} sx={[iconButtonSx, { mt: "-2px", ml: -1 }]}>
                  <IconArrowLeft size={22} />
                </IconButton>
              </Tooltip>
            )}
            <Box sx={{ minWidth: 0 }}>
              {hasTitle && (
                <Typography variant="h3" component="h1" sx={{ overflowWrap: "anywhere" }}>
                  {nameTable}
                </Typography>
              )}
              {subtitle && (
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Stack>
          {withAddButton && (
            <ActionButton variant="contained" startIcon={<IconPlus size={18} />} onClick={() => onPressAdd()} sx={{ flexShrink: 0 }}>
              {addLabel}
            </ActionButton>
          )}
        </Stack>
      )}
      {(selecting || withSearch || (withAddButton && !(hasTitle || canBack))) && (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{
          mb: 1.5,
          minHeight: 48,
          ...(selecting && { px: 1.5, py: 0.5, borderRadius: 999, bgcolor: "primary.light" }),
        }}
        role={selecting ? "region" : undefined}
        aria-label={selecting ? "Selected rows" : undefined}
      >
        {selecting ? (
          <>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography fontWeight={600} aria-live="polite">
                {numSelected} selected
              </Typography>
              <ActionButton variant="text" onClick={() => setSelected([])}>
                Clear
              </ActionButton>
            </Stack>
            <ActionButton
              variant="outlined"
              tone="destructive"
              startIcon={<IconTrash size={16} />}
              onClick={() => onPressDelete(selected)}
            >
              Delete {numSelected === 1 ? "1 item" : `${numSelected} items`}
            </ActionButton>
          </>
        ) : (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {withSearch && (
                <TextField
                  size="small"
                  value={search ?? ""}
                  onChange={handleSearch}
                  placeholder={searchPlaceholder}
                  sx={{ width: { xs: "100%", sm: 340 } }}
                  inputProps={{ "aria-label": searchPlaceholder === "Search" ? "Search this list" : searchPlaceholder }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconSearch size={18} />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Clear the search"
                          onClick={() => handleSearch({ target: { value: "" } })}
                          sx={iconButtonSx}
                        >
                          <IconX size={16} />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              )}
            </Box>
            {withAddButton && !(hasTitle || canBack) && (
              <ActionButton variant="contained" startIcon={<IconPlus size={18} />} onClick={() => onPressAdd()}>
                {addLabel}
              </ActionButton>
            )}
          </>
        )}
      </Stack>
      )}
    </>
  );
}

Toolbar.propTypes = {
  numSelected: PropTypes.number,
  handleSearch: PropTypes.func,
  search: PropTypes.string,
  onPressDelete: PropTypes.func,
  selected: PropTypes.array,
  onPressAdd: PropTypes.func,
  setSelected: PropTypes.func,
  withSearch: PropTypes.bool,
  withAddButton: PropTypes.bool,
  withDeleteItem: PropTypes.bool,
  canBack: PropTypes.bool,
  nameTable: PropTypes.node,
  subtitle: PropTypes.node,
  addLabel: PropTypes.node,
  searchPlaceholder: PropTypes.string,
};

const DynamicTable = ({
  handleSearch = () => {},
  search,
  selected = [],
  setSelected = () => {},
  setPage = () => {},
  page = 0,
  setRowsPerPage = () => {},
  rowsPerPage = 10,
  items = [],
  headCells = [],
  onPressDelete = () => {},
  Listdropdown = [],
  onPressItem = () => {},
  onRequestSort = () => {},
  columns,
  withOption,
  loadingData,
  rowsPerPageOptions = [10, 25, 50],
  totalItem,
  onPressAnyAction = () => {},
  onPressAdd = () => {},
  loadingEditdatatype,
  withPagination = true,
  withSearch = true,
  withAddButton = true,
  withDeleteItem = true,
  withEditItem = true,
  canBack = false,
  nameTable,
  isLogs = false,
  setItems = () => {},
  onDragEnd = () => {},
  isDroppableEnabled = false,
  // What to say when there is nothing here yet. "No data found" tells staff
  // the screen works but not what to do next, which on a screen they have
  // never used before is the whole question.
  emptyTitle,
  emptyHint,
  subtitle,
  addLabel = "Add new",
  searchPlaceholder = "Search",
  loadError,
  onRetry,
  getRowLabel,
}) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  // On a narrow screen the table scrolls sideways inside its card. The
  // actions column stays pinned on the right, with a shadow while columns
  // are hidden under it, and the left edge fades once scrolled: both say
  // "there is more this way".
  const scroller = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const measure = () => {
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(el);
    if (el.firstElementChild) observer?.observe(el.firstElementChild);
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [loadingData, items]);

  const rows = Array.isArray(items) ? items : [];
  // Pages hide a column by passing `false` (no privilege) or `hide: true`.
  const cells = (headCells || []).filter((cell) => cell && !cell.hide);
  const columnCount = cells.length + (withDeleteItem ? 1 : 0) + (isDroppableEnabled ? 1 : 0) + (cells.some((c) => c.id === "action") ? 0 : 1);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const indexitem = rows.findIndex((res) => res.id == result.draggableId); // eslint-disable-line eqeqeq
    const newOrderNumber = rows[result.destination.index]?.order_number;
    const oldOrderNumber = rows[indexitem]?.order_number;
    const reordered = Array.from(rows);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    if (setItems) setItems(reordered);
    if (onDragEnd) onDragEnd({ newOrderNumber, oldOrderNumber, item_id: rows[indexitem]?.id });
  };

  const handleRequestSort = (event, property) => {
    setSelected([]);
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    onRequestSort(property, isAsc ? "desc" : "asc");
  };

  const handleSelectAllClick = (event) => {
    setSelected(event.target.checked ? rows.map((n) => n.id) : []);
  };

  const handleClick = (id) => {
    setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;
  const searching = !!(search && String(search).trim());
  const failed = !!loadError && !loadingData;
  const total = Number(totalItem) || 0;
  // MUI warns (and shows "11–2 of 2") when the page is past the end.
  const safePage = withPagination && total > 0 ? Math.min(page, Math.max(0, Math.ceil(total / rowsPerPage) - 1)) : page;

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box>
        <Toolbar
          numSelected={selected.length}
          withSearch={withSearch}
          search={search}
          handleSearch={(event) => {
            setSelected([]);
            handleSearch(event);
          }}
          setSelected={setSelected}
          onPressDelete={onPressDelete}
          selected={selected}
          onPressAdd={onPressAdd}
          withAddButton={withAddButton}
          withDeleteItem={withDeleteItem}
          canBack={canBack}
          nameTable={nameTable}
          subtitle={subtitle}
          addLabel={addLabel}
          searchPlaceholder={searchPlaceholder}
        />
        <Paper variant="outlined" sx={{ borderRadius: "20px", overflow: "hidden" }}>
          {failed && (
            <Alert
              severity="error"
              sx={{ m: 2, alignItems: "center" }}
              action={
                onRetry ? (
                  <ActionButton onClick={onRetry} startIcon={<IconRefresh size={16} />}>
                    Try again
                  </ActionButton>
                ) : undefined
              }
            >
              {typeof loadError === "string" ? loadError : "This list did not load. Check your connection and try again."}
            </Alert>
          )}
          <Box sx={{ position: "relative" }}>
          {edges.left && (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                width: 24,
                zIndex: 3,
                pointerEvents: "none",
                background: (t) => `linear-gradient(to right, ${t.palette.background.paper}, transparent)`,
              }}
            />
          )}
          <TableContainer
            ref={scroller}
            sx={{
              overflowX: "auto",
              position: "relative",
              "& .table-sticky-actions": {
                position: "sticky",
                right: 0,
                zIndex: 2,
                bgcolor: "background.paper",
                transition: "box-shadow 120ms",
                boxShadow: edges.right ? (t) => `-10px 0 12px -10px ${t.palette.mode === "dark" ? "rgba(0,0,0,0.9)" : "rgba(19,36,59,0.35)"}` : "none",
              },
              "& .MuiTableRow-hover:hover .table-sticky-actions": { bgcolor: "action.hover" },
              "& .Mui-selected .table-sticky-actions": { bgcolor: "primary.light" },
            }}
          >
            <Droppable droppableId="table-droppable">
              {(provided) => (
                <Table
                  sx={{ minWidth: 720 }}
                  aria-label={typeof nameTable === "string" && nameTable ? nameTable : "List"}
                  aria-busy={loadingData || undefined}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  <EnhancedTableHead
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={rows.length}
                    withDeleteItem={withDeleteItem}
                    headCells={cells}
                    withReorder={isDroppableEnabled}
                  />
                  <TableBody>
                    {!loadingData &&
                      rows.map((row, index) => (
                        <Draggable key={row.id} draggableId={String(row.id)} index={index} isDragDisabled={!isDroppableEnabled}>
                          {(dragProvided, snapshot) => (
                            <RowTable
                              provided={isDroppableEnabled ? dragProvided : null}
                              headCells={cells}
                              row={row}
                              index={index}
                              columns={columns}
                              handleClick={handleClick}
                              onPressItem={onPressItem}
                              isSelected={isSelected}
                              Listdropdown={typeof Listdropdown === "function" ? Listdropdown(row) : Listdropdown}
                              isLogs={isLogs}
                              withOption={withOption}
                              onPressAnyAction={onPressAnyAction}
                              loadingEditdatatype={loadingEditdatatype}
                              withDeleteItem={withDeleteItem}
                              withEditItem={withEditItem}
                              snapshot={snapshot}
                              rowLabel={getRowLabel ? getRowLabel(row) : undefined}
                            />
                          )}
                        </Draggable>
                      ))}

                    {loadingData &&
                      Array.from({ length: Math.min(rowsPerPage || 5, 6) }).map((_, i) => (
                        <TableRow key={`skeleton-${i}`} aria-hidden>
                          <TableCell colSpan={columnCount} sx={{ py: 1.25 }}>
                            <Skeleton variant="rounded" height={36} />
                          </TableCell>
                        </TableRow>
                      ))}

                    {!loadingData && !failed && rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={columnCount} sx={{ borderBottom: 0 }}>
                          <Stack
                            alignItems="center"
                            spacing={1}
                            py={6}
                            px={2}
                            textAlign="center"
                            role="status"
                            sx={{ position: "sticky", left: 0, maxWidth: "calc(100vw - 64px)" }}
                          >
                            <Box sx={{ color: "text.secondary" }} aria-hidden>
                              <IconInbox size={32} stroke={1.5} />
                            </Box>
                            <Typography fontWeight={600}>
                              {searching ? `Nothing matches “${String(search).trim()}”.` : emptyTitle ?? "Nothing here yet."}
                            </Typography>
                            {searching ? (
                              <ActionButton onClick={() => handleSearch({ target: { value: "" } })}>Clear the search</ActionButton>
                            ) : emptyHint ? (
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                                {emptyHint}
                              </Typography>
                            ) : withAddButton ? (
                              <Typography variant="body2" color="text.secondary">
                                Use “{addLabel}” to add the first one.
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Droppable>
          </TableContainer>
          </Box>
          {withPagination && (total > 0 || rows.length > 0) && (
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={safePage}
              labelRowsPerPage="Rows per page"
              onPageChange={(event, newPage) => {
                setSelected([]);
                setPage(newPage);
              }}
              onRowsPerPageChange={(event) => {
                setSelected([]);
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              sx={{ borderTop: 1, borderColor: "divider" }}
            />
          )}
        </Paper>
      </Box>
    </DragDropContext>
  );
};

DynamicTable.propTypes = {
  items: PropTypes.array,
  headCells: PropTypes.array,
  columns: PropTypes.array,
  nameTable: PropTypes.node,
  subtitle: PropTypes.node,
  addLabel: PropTypes.node,
  searchPlaceholder: PropTypes.string,
  loadError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onRetry: PropTypes.func,
  getRowLabel: PropTypes.func,
};

export default DynamicTable;
